import React from 'react';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import api from '../api';
import API_URL from '../config';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    ButtonContainer,
    Checklist,
    ChecklistItem,
    DetailContainer,
    Header,
    ProjectInfo,
    Section,
    CheckboxGroup,
} from './Detail.styles';
import { Vloerverwarming, Klimaatwand, Klimaatplafond } from '../types';
import ProjectSectionForm from './ProjectSectionForm';
import logo from '../assets/logo.png';
import LoadingOverlay from './LoadingOverlay';

const AddProjectPage: React.FC = () => {
    const navigate = useNavigate();

    return (
        <DetailContainer>
            <Header>
                <div>
                    <img src={logo} alt="Apply Heating Logo" />
                </div>
                <h2>Add New Project</h2>
            </Header>
            <Formik
                initialValues={{
                    title: '',
                    klant: '',
                    adres: '',
                    info: '',
                    vloerverwarming: [] as Vloerverwarming[],
                    klimaatwand: '' as Klimaatwand,
                    klimaatplafond: '' as Klimaatplafond,
                    volgensLegplan: false,
                    kringnummersAangegevenOpVerdelers: false,
                    installatieDicht: false,
                    sections: [],
                }}
                validationSchema={Yup.object({
                    klant: Yup.string().required('Required'),
                    adres: Yup.string().required('Required'),
                    info: Yup.string().required('Required'),
                })}
                onSubmit={async (values, { resetForm }) => {
                    const formData = new FormData();
                    formData.append('title', `project-${values.klant}`);
                    formData.append('klant', values.klant);
                    formData.append('adres', values.adres);
                    formData.append('info', values.info);
                    formData.append('vloerverwarming', JSON.stringify(values.vloerverwarming));
                    formData.append('klimaatwand', values.klimaatwand);
                    formData.append('klimaatplafond', values.klimaatplafond);
                    formData.append('volgensLegplan', String(values.volgensLegplan));
                    formData.append('kringnummersAangegevenOpVerdelers', String(values.kringnummersAangegevenOpVerdelers));
                    formData.append('installatieDicht', String(values.installatieDicht));
                    
                    let fileIdCounter = 0;
                    const sectionsWithImages = values.sections.map((section: any) => {
                        let planName = null;
                        if (section.plan instanceof File) {
                            const uniqueFileName = `${fileIdCounter++}_${section.plan.name}`;
                            const newFile = new File([section.plan], uniqueFileName, { type: section.plan.type });
                            formData.append('images', newFile);
                            planName = uniqueFileName;
                        }

                        const sectionImages = section.images.map((image: File) => {
                            const uniqueFileName = `${fileIdCounter++}_${image.name}`;
                            const newFile = new File([image], uniqueFileName, { type: image.type });
                            formData.append('images', newFile);
                            return uniqueFileName;
                        });

                        return { ...section, plan: planName, images: sectionImages };
                    });
                    formData.append('sections', JSON.stringify(sectionsWithImages));

                    try {
                        await api.post(`/api/projects`, formData, {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                                'Content-Type': 'multipart/form-data',
                            },
                        });
                        resetForm();
                        navigate('/');
                    } catch (error) {
                        console.error('Error creating project', error);
                    }
                }}
            >
                {({ values, handleChange, handleBlur, setFieldValue, handleSubmit, isSubmitting }) => (
                    <>
                        {isSubmitting && <LoadingOverlay />}
                        <Form onSubmit={handleSubmit}>
                            <ProjectInfo>
                            <p><strong>Klant:</strong> <input id="klant" name="klant" type="text" onChange={handleChange} onBlur={handleBlur} value={values.klant} /></p>
                            <p><strong>Adres:</strong> <input id="adres" name="adres" type="text" onChange={handleChange} onBlur={handleBlur} value={values.adres} /></p>
                            <p><strong>Info:</strong> <textarea id="info" name="info" onChange={handleChange} onBlur={handleBlur} value={values.info} /></p>
                        </ProjectInfo>
                        <Section>
                            <h3>Vloerverwarming</h3>
                            <CheckboxGroup>
                                {Object.values(Vloerverwarming).map((value) => (
                                    <label key={value}>
                                        <input
                                            type="checkbox"
                                        name="vloerverwarming"
                                        value={value}
                                        checked={values.vloerverwarming.includes(value)}
                                        onChange={(e) => {
                                            const { checked } = e.target;
                                            const newValues = checked
                                                ? [...values.vloerverwarming, value]
                                                : values.vloerverwarming.filter((v) => v !== value);
                                            setFieldValue('vloerverwarming', newValues);
                                        }}
                                        onBlur={handleBlur}
                                    />
                                    {value}
                                </label>
                                ))}
                            </CheckboxGroup>
                        </Section>
                        <Section>
                            <h3>Klimaatwand</h3>
                            <CheckboxGroup>
                                {Object.values(Klimaatwand).map((value) => (
                                    <label key={value}>
                                        <input
                                            type="checkbox"
                                        name="klimaatwand"
                                        value={value}
                                        checked={values.klimaatwand === value}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setFieldValue('klimaatwand', value);
                                            } else {
                                                setFieldValue('klimaatwand', '');
                                            }
                                        }}
                                        onBlur={handleBlur}
                                    />
                                    {value}
                                </label>
                                ))}
                            </CheckboxGroup>
                        </Section>
                        <Section>
                            <h3>Klimaatplafond</h3>
                            <CheckboxGroup>
                                {Object.values(Klimaatplafond).map((value) => (
                                    <label key={value}>
                                        <input
                                            type="checkbox"
                                        name="klimaatplafond"
                                        value={value}
                                        checked={values.klimaatplafond === value}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setFieldValue('klimaatplafond', value);
                                            } else {
                                                setFieldValue('klimaatplafond', '');
                                            }
                                        }}
                                        onBlur={handleBlur}
                                    />
                                    {value}
                                </label>
                                ))}
                            </CheckboxGroup>
                        </Section>
                        <Checklist>
                            <h4>CHECKLIST</h4>
                            <ChecklistItem>
                                <span>Volgens legplan:</span>
                                <input type="checkbox" name="volgensLegplan" onChange={handleChange} onBlur={handleBlur} checked={values.volgensLegplan} />
                            </ChecklistItem>
                            <ChecklistItem>
                                <span>Kringnummers aangegeven op verdelers?</span>
                                <input type="checkbox" name="kringnummersAangegevenOpVerdelers" onChange={handleChange} onBlur={handleBlur} checked={values.kringnummersAangegevenOpVerdelers} />
                            </ChecklistItem>
                            <ChecklistItem>
                                <span>Installatie dicht:</span>
                                <input type="checkbox" name="installatieDicht" onChange={handleChange} onBlur={handleBlur} checked={values.installatieDicht} />
                            </ChecklistItem>
                        </Checklist>
                        <FieldArray
                            name="sections"
                            render={(arrayHelpers) => (
                                <div>
                                    {values.sections.map((section, index) => (
                                        <div key={index}>
                                            <ProjectSectionForm index={index} section={section} setFieldValue={setFieldValue} />
                                            <ButtonContainer>
                                                <Button type="button" onClick={() => arrayHelpers.remove(index)}>
                                                    Remove Section
                                                </Button>
                                            </ButtonContainer>
                                        </div>
                                    ))}
                                    <ButtonContainer>
                                        <Button
                                            type="button"
                                            onClick={() =>
                                                arrayHelpers.push({
                                                    sectionName: '',
                                                    afgeperstMet: '',
                                                    hoeveelheidDruk: 0,
                                                    buitenTemperatuur: 0,
                                                    plan: null,
                                                    images: [],
                                                })
                                            }
                                        >
                                            Add Section
                                        </Button>
                                    </ButtonContainer>
                                </div>
                            )}
                        />
                        <ButtonContainer>
                            <Button type="submit">Submit</Button>
                        </ButtonContainer>
                    </Form>
                </>
                )}
            </Formik>
        </DetailContainer>
    );
};

export default AddProjectPage;
